import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';

let uiInputUid = 0;

export type UiInputKind =
  | 'text'
  | 'number'
  | 'amount'
  | 'nss'
  | 'email'
  | 'password'
  | 'search'
  | 'url';

/**
 * Átomo: campo de una línea. Sin HTTP ni stores.
 */
@Component({
  selector: 'ui-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  host: {
    class: 'ui-input-host',
    '[class.ui-input-host--block]': 'block()',
  },
})
export class UiInputComponent {
  readonly kind = input<UiInputKind>('text');
  readonly label = input('');
  readonly value = model<string>('');
  readonly placeholder = input('');

  /** Si es false, oculta el encabezado (label + hint Obligatorio/Opcional). */
  readonly showLabel = input(true);

  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  /** Controla si el mensaje se muestra o no, independientemente de su contenido. */
  readonly showMessage = input(true);

  readonly name = input<string | undefined>(undefined);
  readonly autocomplete = input<string | undefined>(undefined);
  readonly maxlength = input<number | undefined>(undefined);
  readonly minlength = input<number | undefined>(undefined);
  readonly step = input<string | undefined>(undefined);

  /**
   * Solo `kind="amount"`. Cantidad de decimales permitidos (default 2).
   * Ej. tasas/cuotas fiscales: `[decimalPlaces]="6"`.
   */
  readonly decimalPlaces = input(2);

  readonly block = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  /**
   * Solo `kind="password"`. Si es true, muestra checklist visual de reglas
   * (8 caracteres, alfanumérico y, opcionalmente, coincidencia).
   */
  readonly showPasswordRules = input(false);

  /**
   * Solo `kind="password"`. Si se define (incluso `''`), muestra la regla
   * “Las contraseñas coinciden”. Independiente de `showPasswordRules`.
   */
  readonly matchValue = input<string | undefined>(undefined);

  readonly valueChange = output<string>();
  readonly blurred = output<FocusEvent>();
  readonly focused = output<FocusEvent>();

  protected readonly generatedId = `ui-input-${++uiInputUid}`;

  protected readonly inputId = computed(
    () => this.fieldId() ?? this.generatedId,
  );

  /** Estado interno: controla si la contraseña es visible. */
  protected readonly showPassword = signal(false);

  protected readonly nativeType = computed(() => {
    const k = this.kind();
    if (k === 'amount' || k === 'nss') return 'text';
    if (k === 'password') return this.showPassword() ? 'text' : 'password';
    return k;
  });

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected readonly inputModeAttr = computed(() => {
    if (this.kind() === 'amount') return 'decimal';
    if (this.kind() === 'nss') return 'numeric';
    return null as string | null;
  });

  /** true para number, amount y nss: texto alineado a la derecha. */
  protected readonly isNumericKind = computed(() => {
    const k = this.kind();
    return k === 'number' || k === 'amount' || k === 'nss';
  });

  /** Evita padear decimales de amount mientras el usuario escribe. */
  private readonly amountFocused = signal(false);

  /**
   * Valor formateado para mostrar en el input.
   * Para amount: agrega separadores de miles y, en reposo, completa decimales.
   * Para nss: máscara XXXX-XX-XXXX-X.
   * Para el resto: igual al valor del modelo.
   */
  protected readonly displayValue = computed(() => {
    if (this.kind() === 'amount') return this.formatAmountDisplay(this.value());
    if (this.kind() === 'nss') return this.formatNss(this.value());
    return this.value();
  });

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly messageId = computed(() => `${this.inputId()}-message`);

  protected readonly passwordRulesId = computed(
    () => `${this.inputId()}-password-rules`,
  );

  protected readonly showPasswordRulesList = computed(
    () => this.kind() === 'password' && this.showPasswordRules(),
  );

  protected readonly showPasswordMatchRule = computed(
    () => this.kind() === 'password' && this.matchValue() !== undefined,
  );

  protected readonly passwordHasMinLength = computed(
    () => this.value().length >= 8,
  );

  protected readonly passwordIsAlphanumeric = computed(() => {
    const v = this.value();
    return /[A-Za-z]/.test(v) && /\d/.test(v);
  });

  protected readonly passwordHasCasing = computed(() => {
    const v = this.value();
    return /[a-z]/.test(v) && /[A-Z]/.test(v);
  });

  protected readonly passwordMatches = computed(() => {
    const match = this.matchValue();
    if (match === undefined) return false;
    const v = this.value();
    return v.length > 0 && match.length > 0 && v === match;
  });

  protected readonly ariaDescribedBy = computed(() => {
    const parts: string[] = [];
    if (this.message()) parts.push(this.messageId());
    if (this.showPasswordRulesList() || this.showPasswordMatchRule()) {
      parts.push(this.passwordRulesId());
    }
    return parts.length ? parts.join(' ') : null;
  });

  protected onInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    if (this.kind() === 'amount') {
      this.applyAmountMask(el);
      return;
    }
    if (this.kind() === 'nss') {
      this.applyNssMask(el);
      return;
    }
    this.value.set(el.value);
    this.valueChange.emit(el.value);
  }

  protected onBlur(event: FocusEvent): void {
    if (this.kind() === 'amount') {
      this.amountFocused.set(false);
      this.finalizeAmount(event.target as HTMLInputElement);
    }
    this.blurred.emit(event);
  }

  protected onFocus(event: FocusEvent): void {
    if (this.kind() === 'amount') this.amountFocused.set(true);
    this.focused.emit(event);
  }

  /** Decimales efectivos para amount (≥ 0). */
  private resolvedDecimalPlaces(): number {
    const n = Math.floor(this.decimalPlaces());
    return Number.isFinite(n) && n >= 0 ? n : 2;
  }

  /**
   * Aplica máscara de importe mientras el usuario escribe:
   * - Solo permite dígitos y un punto decimal (máx. `decimalPlaces` cifras).
   * - Inserta comas como separador de miles automáticamente.
   * - Guarda el valor limpio (sin comas) en el modelo.
   */
  private applyAmountMask(el: HTMLInputElement): void {
    const places = this.resolvedDecimalPlaces();
    const selStart = el.selectionStart ?? el.value.length;

    // Contar chars no-coma antes del cursor para reposicionarlo después
    const nonCommasBefore = el.value
      .slice(0, selStart)
      .replace(/,/g, '').length;

    // Limpiar: quitar comas, luego quitar todo excepto dígitos y primer punto
    const withoutCommas = el.value.replace(/,/g, '');
    const cleaned = withoutCommas.replace(/[^0-9.]/g, '');
    const dotIdx = cleaned.indexOf('.');

    let intStr: string;
    let decStr: string | undefined;

    if (dotIdx !== -1) {
      intStr = cleaned.slice(0, dotIdx);
      if (places > 0) {
        decStr = cleaned.slice(dotIdx + 1, dotIdx + 1 + places);
      }
    } else {
      intStr = cleaned;
    }

    const intFormatted = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const display =
      decStr !== undefined ? `${intFormatted}.${decStr}` : intFormatted;

    el.value = display;

    // Reposicionar cursor: buscar posición tras `nonCommasBefore` chars no-coma
    let newPos = display.length;
    let count = 0;
    for (let i = 0; i < display.length; i++) {
      if (display[i] !== ',') count++;
      if (count === nonCommasBefore) {
        newPos = i + 1;
        break;
      }
    }
    if (nonCommasBefore === 0) newPos = 0;
    el.setSelectionRange(newPos, newPos);

    // Emitir valor limpio (sin comas) como valor del modelo
    const raw = intStr + (decStr !== undefined ? '.' + decStr : '');
    this.value.set(raw);
    this.valueChange.emit(raw);
  }

  /**
   * Al perder foco: redondea a `decimalPlaces` y actualiza el modelo.
   * displayValue() se encargará de formatearlo con comas para mostrarlo.
   */
  private finalizeAmount(el: HTMLInputElement): void {
    const raw = this.value();
    if (!raw) return;

    const num = parseFloat(raw);
    if (isNaN(num)) {
      el.value = '';
      this.value.set('');
      this.valueChange.emit('');
      return;
    }

    const finalized = num.toFixed(this.resolvedDecimalPlaces());
    this.value.set(finalized);
    this.valueChange.emit(finalized);
  }

  /** Formatea un string numérico limpio para mostrar en el input. */
  private formatAmountDisplay(raw: string): string {
    if (!raw) return '';

    if (!this.amountFocused()) {
      const num = parseFloat(raw);
      if (!isNaN(num)) {
        const [intPart, decPart] = num.toFixed(this.resolvedDecimalPlaces()).split('.');
        const intFormatted = (intPart || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
      }
    }

    const parts = raw.split('.');
    const intFormatted = (parts[0] || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.length > 1 ? `${intFormatted}.${parts[1]}` : intFormatted;
  }

  /**
   * Máscara Número de seguridad social: XXXX-XX-XXXX-X (11 dígitos).
   * El modelo guarda el valor formateado con guiones.
   */
  private applyNssMask(el: HTMLInputElement): void {
    const selStart = el.selectionStart ?? el.value.length;
    const digitsBefore = el.value.slice(0, selStart).replace(/\D/g, '').length;
    const formatted = this.formatNss(el.value);

    el.value = formatted;

    let newPos = formatted.length;
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) count++;
      if (count === digitsBefore) {
        newPos = i + 1;
        break;
      }
    }
    if (digitsBefore === 0) newPos = 0;
    el.setSelectionRange(newPos, newPos);

    this.value.set(formatted);
    this.valueChange.emit(formatted);
  }

  /** Formatea dígitos al patrón XXXX-XX-XXXX-X (máx. 11 dígitos). */
  private formatNss(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 11);
    const p1 = digits.slice(0, 4);
    const p2 = digits.slice(4, 6);
    const p3 = digits.slice(6, 10);
    const p4 = digits.slice(10, 11);
    let out = p1;
    if (p2) out += `-${p2}`;
    if (p3) out += `-${p3}`;
    if (p4) out += `-${p4}`;
    return out;
  }
}
